import {
  Document,
  Paragraph,
  TextRun,
  Packer,
  AlignmentType,
  BorderStyle,
} from "docx";
import type { MotivatiebriefContent } from "../ai/document-gen";

const C = {
  heading: "0F172A",
  accent:  "4338CA",
  body:    "334155",
  muted:   "64748B",
  rule:    "E2E8F0",
};

const pt = (n: number) => n * 2;

function bodyParagraph(text: string, spacingAfter = 220): Paragraph {
  return new Paragraph({
    spacing: { after: spacingAfter },
    alignment: AlignmentType.JUSTIFIED,
    children: [new TextRun({ text, size: pt(11), color: C.body, font: "Calibri" })],
  });
}

/**
 * Renders a MotivatiebriefContent object into a professional Dutch letter .docx.
 */
export async function formatLetter(
  content: MotivatiebriefContent,
  consultantName: string,
  vacancyTitle: string,
  clientName: string,
): Promise<Uint8Array> {
  const today = new Date().toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const children: Paragraph[] = [];

  // ── Sender block ──────────────────────────────────────────────────────
  children.push(
    new Paragraph({
      spacing: { after: 60 },
      children: [
        new TextRun({
          text: consultantName,
          bold: true,
          size: pt(13),
          color: C.heading,
          font: "Calibri",
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 8 },
      children: [
        new TextRun({
          text: "Adapt Interim Professionals",
          size: pt(10),
          color: C.muted,
          font: "Calibri",
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 400 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: C.rule } },
      children: [
        new TextRun({
          text: today,
          size: pt(10),
          color: C.muted,
          font: "Calibri",
        }),
      ],
    }),
  );

  // ── Subject line ──────────────────────────────────────────────────────
  children.push(
    new Paragraph({
      spacing: { before: 320, after: 60 },
      children: [
        new TextRun({
          text: "Betreft: ",
          bold: true,
          size: pt(11),
          color: C.heading,
          font: "Calibri",
        }),
        new TextRun({
          text: `Sollicitatie ${vacancyTitle} — ${clientName}`,
          size: pt(11),
          color: C.body,
          font: "Calibri",
        }),
      ],
    }),
    new Paragraph({ spacing: { after: 300 }, children: [] }),
  );

  // ── Salutation ────────────────────────────────────────────────────────
  children.push(
    new Paragraph({
      spacing: { after: 220 },
      children: [
        new TextRun({
          text: content.salutation,
          size: pt(11),
          color: C.body,
          font: "Calibri",
        }),
      ],
    }),
  );

  // ── Opening paragraph ─────────────────────────────────────────────────
  children.push(bodyParagraph(content.opening_paragraph));

  // ── Body paragraphs ───────────────────────────────────────────────────
  content.body_paragraphs.forEach((p) => children.push(bodyParagraph(p)));

  // ── Closing paragraph ─────────────────────────────────────────────────
  children.push(bodyParagraph(content.closing_paragraph, 400));

  // ── Sign off + name ───────────────────────────────────────────────────
  children.push(
    new Paragraph({
      spacing: { after: 60 },
      children: [
        new TextRun({
          text: content.sign_off,
          size: pt(11),
          color: C.body,
          font: "Calibri",
        }),
      ],
    }),
    new Paragraph({
      spacing: { before: 320 },
      children: [
        new TextRun({
          text: consultantName,
          bold: true,
          size: pt(11),
          color: C.heading,
          font: "Calibri",
        }),
      ],
    }),
  );

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: "Calibri", size: pt(11), color: C.body },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: { top: 1080, right: 1080, bottom: 1080, left: 1080 },
          },
        },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const arrayBuffer = await blob.arrayBuffer();
  return new Uint8Array(arrayBuffer);
}
